import axios from "axios";
import { getAuthToken } from "./auth";
import * as XLSX from "xlsx-js-style";

export const REJECTED_APPS_EXPORT_CHUNK_THRESHOLD = 2000;
export const REJECTED_APPS_EXPORT_PAGE_SIZE = 2000;

export const REJECTED_APPS_EXPORT_COLUMN_WIDTHS = [
    { wch: 22 }, // Application Number
    { wch: 26 }, // RO Name
    { wch: 14 }, // RO ID
    { wch: 38 }, // Application ID
    { wch: 16 }, // Application Date
    { wch: 16 }, // Rejected Date
    { wch: 18 }, // Parent Stage
    { wch: 22 }, // Application Stage
    { wch: 40 }, // Rejection Reason
    { wch: 26 }, // Customer Name
    { wch: 20 }, // Customer ID
    { wch: 16 }, // Branch ID
    { wch: 26 }, // Branch
    { wch: 26 }, // BM Name
    { wch: 26 }, // BCM Name
];

export const REJECTED_APPS_HEADER_STYLE = {
    font: { bold: true },
    fill: { fgColor: { rgb: "D9E1F2" } },
};

export const applyRejectedAppsHeaderStyle = (worksheet, columnCount) => {
    for (let col = 0; col < columnCount; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[cellRef]) {
            worksheet[cellRef].s = REJECTED_APPS_HEADER_STYLE;
        }
    }
};

const formatDateForExport = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-GB");
};

const buildOfficerName = (officer) => {
    if (!officer) return "";
    return [officer?.first_name, officer?.middle_name, officer?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();
};

export const mapRejectedApplicationRowsForExport = (rows) => {
    return rows.map((application) => {
        const roLabel = buildOfficerName(application?.ro);
        const bmLabel = buildOfficerName(application?.bm);
        const bcmLabel = buildOfficerName(application?.bcm);

        return {
            "Application Number": application?.seeds_id,
            "RO Name": roLabel,
            "RO ID": application?.ro?.emp_id,
            "Application ID": application?.application_id,
            "Application Date": formatDateForExport(application?.createdAt?.split("T")?.[0]),
            "Rejected Date": formatDateForExport(application?.rejected_at?.split("T")?.[0]),
            "Parent Stage": application?.parent_stage_name,
            "Application Stage": application?.application_stage_name,
            "Rejection Reason": application?.rejected_reason || "",
            "Customer Name": [
                application?.first_name,
                application?.middle_name,
                application?.last_name,
            ]
                .filter(Boolean)
                .join(" ")
                .trim(),
            "Customer ID": application?.external_customer_id,
            "Branch ID": application?.branch_code,
            "Branch": application?.branch_name,
            "BM Name": bmLabel,
            "BCM Name": bcmLabel,
        };
    });
};

/**
 * Fetches all rejected application pages (for "All Pages" scope).
 * Uses the SAME endpoint & filters that the dashboard uses for listing.
 */
export const fetchAllRejectedApplicationPages = async (queryFilters, limit, onProgress) => {
    let authToken = getAuthToken();

    let headers = {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
    };

    const MAX_PAGES_SAFEGUARD = 1000;

    let allRows = [];
    let currentPage = 1;
    let totalPages = 1;

    do {
        // Mirror the sanitization done on the backend call for the list
        const apiFilters = { ...queryFilters };

        if (apiFilters.global_search) {
            delete apiFilters.global_search;
        }

        const queryParams = new URLSearchParams({
            page: currentPage,
            limit,
            filters: JSON.stringify(apiFilters),
        });

        const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/applications/get-all-applications`,
            {
                headers,
                params: {
                    page: currentPage,
                    limit,
                    filters: JSON.stringify(apiFilters),
                },
            }
        );

        const pageRows = response?.data?.data?.applications || [];
        const totalCount = response?.data?.data?.pagination?.total || 0;

        allRows = allRows.concat(pageRows);
        totalPages = Math.ceil(totalCount / limit) || 1;

        onProgress(currentPage, totalPages);

        // Safeguard 1: stop if backend returns empty page
        if (pageRows.length === 0) break;

        // Safeguard 2: hard cap on page count
        if (currentPage >= MAX_PAGES_SAFEGUARD) {
            console.warn(`Reached page cap (${MAX_PAGES_SAFEGUARD}). Aborting export fetch.`);
            break;
        }

        currentPage++;
    } while (currentPage <= totalPages);

    return allRows;
};

export const triggerBrowserDownload = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
};

export const buildRejectedAppsXlsxAndDownload = async (rawRows, filename) => {
    const rows = mapRejectedApplicationRowsForExport(rawRows);

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = REJECTED_APPS_EXPORT_COLUMN_WIDTHS;

    applyRejectedAppsHeaderStyle(worksheet, REJECTED_APPS_EXPORT_COLUMN_WIDTHS.length);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rejected Applications");

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    triggerBrowserDownload(blob, filename);
};